import { createContext, useCallback, useContext } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import { UserApi, UserCreateApi, UserListApi, UserUpdateApi } from './UsersApiProviderTypes';

interface UsersApiContextType {
  getUsers: (skip?: number, limit?: number) => Promise<UserListApi>;
  getUserById: (userId: string) => Promise<UserApi>;
  createUser: (data: UserCreateApi) => Promise<UserApi>;
  updateUser: (userId: string, data: UserUpdateApi) => Promise<UserApi>;
  deleteUser: (userId: string) => Promise<void>;
  changeUserPassword: (userId: string, password: string) => Promise<void>;
  sendResetPasswordEmail: (email: string) => Promise<void>;
}

const UsersApiContext = createContext<UsersApiContextType | null>(null);

export function UsersApiProvider({ children }) {
  const { get, post, patch, del } = useApiService(CONFIG.SERVER_ORGANIZATION);

  const getUsers = useCallback(
    async (skip: number = 0, limit: number = 10): Promise<UserListApi> => {
      try {
        const response = await get(`/v1/users?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
      }
    },
    [get],
  );

  const getUserById = useCallback(
    async (userId: string): Promise<UserApi> => {
      try {
        const response = await get(`/v1/users/${userId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching user by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createUser = useCallback(
    async (user: UserCreateApi): Promise<UserApi> => {
      try {
        const response = await post(`/v1/users`, user);
        return response.data;
      } catch (error) {
        console.error('Error creating user:', error);
        throw error;
      }
    },
    [post],
  );

  const changeUserPassword = useCallback(
    async (userId: string, password: string): Promise<void> => {
      try {
        const response = await post(`/v1/users/${userId}/change_password`, password);
        return response.data;
      } catch (error) {
        console.error('Error changing user password:', error);
        throw error;
      }
    },
    [post],
  );

  const sendResetPasswordEmail = useCallback(
    async (email: string): Promise<void> => {
      try {
        const response = await post(`/v1/users/send_reset_password_email`, { email });
        return response.data;
      } catch (error) {
        console.error('Error sending reset password email:', error);
        throw error;
      }
    },
    [post],
  );

  const updateUser = useCallback(
    async (userId: string, user: UserUpdateApi): Promise<UserApi> => {
      try {
        const response = await patch(`/v1/users/${userId}`, user);
        return response.data;
      } catch (error) {
        console.error('Error updating user:', error);
        throw error;
      }
    },
    [patch],
  );

  const deleteUser = useCallback(
    async (userId: string): Promise<void> => {
      try {
        const response = await del(`/v1/users/${userId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
      }
    },
    [del],
  );

  return (
    <UsersApiContext.Provider
      value={{
        getUsers,
        getUserById,
        createUser,
        changeUserPassword,
        sendResetPasswordEmail,
        updateUser,
        deleteUser,
      }}
    >
      {children}
    </UsersApiContext.Provider>
  );
}

export function useUsersApi(): UsersApiContextType {
  const context = useContext(UsersApiContext);
  if (!context) {
    throw new Error('useUserApi must be used within a UsersApiProvider');
  }
  return context;
}
